import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { SubClient, Pays } from '../../types';
import { subClientService, paysService } from '../../services/api';

interface SubClientCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (subClient: SubClient) => void;
}

const SubClientCreateModal: React.FC<SubClientCreateModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    type: 'individual' as 'individual' | 'company',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    notes: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paysList, setPaysList] = useState<Pays[]>([]);

  useEffect(() => {
    if (open) {
      paysService.getAll().then((r) => setPaysList(r.data.data || [])).catch(() => {});
    }
  }, [open]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!formData.nom || !formData.prenom) {
      setError('Le nom et le prénom sont requis');
      return;
    }
    if (!formData.email) {
      setError('L\'email est requis');
      return;
    }
    if (formData.type === 'company' && !formData.company_name) {
      setError('Le nom de l\'entreprise est requis pour les entreprises');
      return;
    }

    setLoading(true);
    try {
      const response = await subClientService.create(formData);
      onSuccess(response.data.data);
      onClose();
      // Reset form
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        phone: '',
        type: 'individual',
        company_name: '',
        siret: '',
        address: '',
        city: '',
        postal_code: '',
        country: '',
        notes: '',
        is_active: true
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création du sous-client');
      console.error('Error creating sub-client:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Créer un nouveau sous-client</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Type"
            >
              <MenuItem value="individual">Individuel</MenuItem>
              <MenuItem value="company">Entreprise</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.nom}
                onChange={(e) => handleInputChange('nom', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Prénom"
                value={formData.prenom}
                onChange={(e) => handleInputChange('prenom', e.target.value)}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Box>
            {formData.type === 'company' && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="Nom de l'entreprise"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="SIRET"
                  value={formData.siret}
                  onChange={(e) => handleInputChange('siret', e.target.value)}
                />
              </Box>
            )}
            <TextField
              fullWidth
              label="Adresse"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
              <TextField
                fullWidth
                label="Code postal"
                value={formData.postal_code}
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>Pays</InputLabel>
                <Select
                  label="Pays"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  <MenuItem value="">—</MenuItem>
                  {paysList.map((p) => (
                    <MenuItem key={p.id} value={p.nom_fr_fr}>{p.nom_fr_fr}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Création...' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubClientCreateModal;

